import { apiClient } from "./client";

/**
 * Lightweight representation of TipTap's JSONContent so that
 * `@slumbr/core` doesn't need `@tiptap/react` as a dependency.
 */
export type NoteContent = Record<string, unknown> | null;

export type NoteShareRole = "view" | "edit";
export type NoteAccessRole = "owner" | NoteShareRole;

export interface NoteShareEntry {
  userId: string;
  role: NoteShareRole;
  addedAt: string;
}

export interface NoteResponse {
  id: string;
  title: string;
  content: NoteContent;
  parentId: string | null;
  tags: string[];
  order?: number;
  isFavorite: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  icon?: string;
  coverImage?: string;
  fullWidth?: boolean;
  /** Marks this record as an in-note page (child of parentId). */
  isPage?: boolean;
  /** Optional page colour (hex). */
  color?: string | null;
  /** Ordered ids of this note's in-note pages (parent notes only). */
  pageOrder?: string[];
  ownerId?: string;
  sharedWith?: NoteShareEntry[];
  /** Present on shared notes — the requester's role for this note. */
  myRole?: NoteAccessRole;
  /** Base64-encoded canonical Yjs binary state for collab. */
  yjsState?: string;
}

export interface ListNotesResponse {
  items: NoteResponse[];
  nextCursor?: string;
}

export interface CreateNotePayload {
  id?: string;
  title?: string;
  content?: NoteContent;
  parentId?: string | null;
  tags?: string[];
  order?: number;
  icon?: string;
  coverImage?: string | null;
  fullWidth?: boolean;
  isPage?: boolean;
  color?: string | null;
}

export async function listNotes(
  cursor?: string
): Promise<ListNotesResponse> {
  const params = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return apiClient.get<ListNotesResponse>(`/api/notes${params}`);
}

/**
 * Fetch ALL of the user's notes, following the server cursor to completion.
 *
 * `list-notes` caps each response at 50 rows. In-note pages share the user's
 * partition (USER#…/NOTE#…), so a page-heavy user crosses 50 rows quickly and a
 * single `listNotes()` call silently drops everything past the first page —
 * notes whose `sk` sorts late vanish from the workspace list. This walks every
 * page so the client always has the complete set. The 200-iteration guard caps
 * a pathological/looping cursor at 10k notes.
 */
export async function listAllNotes(): Promise<ListNotesResponse> {
  const items: NoteResponse[] = [];
  let cursor: string | undefined;
  let guard = 0;
  do {
    const page = await listNotes(cursor);
    items.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor && ++guard < 200);
  return { items };
}

export async function getNote(
  id: string,
  options?: { parentId?: string | null }
): Promise<NoteResponse> {
  // `parent` lets a shared-note recipient fetch one of the note's pages: the
  // page has no share pointer of its own, so the backend validates access via
  // the parent note's pointer instead.
  const params = options?.parentId
    ? `?parent=${encodeURIComponent(options.parentId)}`
    : "";
  return apiClient.get<NoteResponse>(`/api/notes/${id}${params}`);
}

export async function createNote(
  data: CreateNotePayload
): Promise<NoteResponse> {
  return apiClient.post<NoteResponse>("/api/notes", data);
}

export async function updateNote(
  id: string,
  updates: Partial<NoteResponse>,
  options?: { parentId?: string | null }
): Promise<NoteResponse> {
  const params = options?.parentId
    ? `?parent=${encodeURIComponent(options.parentId)}`
    : "";
  return apiClient.put<NoteResponse>(`/api/notes/${id}${params}`, updates);
}

export async function deleteNote(
  id: string,
  options?: { hard?: boolean; parentId?: string | null }
): Promise<void> {
  const params = new URLSearchParams();
  if (options?.hard) params.set("hard", "true");
  // `parent` authorises an editor deleting a page of a note shared with them.
  if (options?.parentId) params.set("parent", options.parentId);
  const qs = params.toString();
  await apiClient.delete(`/api/notes/${id}${qs ? `?${qs}` : ""}`);
}

// ─── Note sharing ────────────────────────────────────────────────────────────

export interface ShareNotePayload {
  userId: string;
  role: NoteShareRole;
}

export interface ShareNoteResponse {
  noteId: string;
  sharedWith: NoteShareEntry[];
}

export async function shareNote(
  noteId: string,
  payload: ShareNotePayload
): Promise<ShareNoteResponse> {
  return apiClient.post<ShareNoteResponse>(`/api/notes/${noteId}/share`, payload);
}

export async function unshareNote(
  noteId: string,
  userId: string
): Promise<ShareNoteResponse> {
  return apiClient.delete<ShareNoteResponse>(
    `/api/notes/${noteId}/share/${userId}`
  );
}

export async function leaveSharedNote(noteId: string): Promise<void> {
  await apiClient.delete(`/api/notes/${noteId}/share/me`);
}

export async function listSharedNotes(): Promise<ListNotesResponse> {
  return apiClient.get<ListNotesResponse>("/api/notes/shared");
}

export interface NoteCommentResponse {
  id: string;
  noteId: string;
  text: string;
  author: string;
  createdAt: string;
  updatedAt: string;
  isResolved: boolean;
  threadId: string;
  highlightStart?: number;
  highlightEnd?: number;
}

export interface ListNoteCommentsResponse {
  items: NoteCommentResponse[];
}

export type CreateNoteCommentPayload = Omit<
  NoteCommentResponse,
  "createdAt" | "updatedAt" | "isResolved"
>;

export async function listNoteComments(
  noteId?: string
): Promise<ListNoteCommentsResponse> {
  const params = noteId ? `?noteId=${encodeURIComponent(noteId)}` : "";
  return apiClient.get<ListNoteCommentsResponse>(`/api/notes/comments${params}`);
}

export async function createNoteComment(
  data: CreateNoteCommentPayload
): Promise<NoteCommentResponse> {
  return apiClient.post<NoteCommentResponse>("/api/notes/comments", data);
}

export async function updateNoteComment(
  id: string,
  noteId: string,
  updates: Partial<Pick<NoteCommentResponse, "text" | "isResolved" | "threadId" | "highlightStart" | "highlightEnd">>
): Promise<NoteCommentResponse> {
  return apiClient.put<NoteCommentResponse>(`/api/notes/comments/${id}`, {
    noteId,
    ...updates,
  });
}

export async function deleteNoteComment(
  id: string,
  noteId: string
): Promise<void> {
  await apiClient.delete(
    `/api/notes/comments/${id}?noteId=${encodeURIComponent(noteId)}`
  );
}
